import { Effect } from 'effect';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { sessionPagesEffect } from '../client';
import { useRequestOwner } from './use-request-owner';
import type { OkraSession, Page } from '../types';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import { useOkra } from './provider';

export interface UsePagesOptions {
  /** Skip fetching (default: true) */
  enabled?: boolean;
}

export interface UsePagesReturn {
  data: Page[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Native page-listing program — the IO seam behind {@link usePages}.
 *
 * The failure is the redacted {@link OkraHookError}; the original rejection
 * (including `AbortError`) is restored by the Promise adapter.
 */
export function fetchPagesEffect(
  session: OkraSession,
  signal?: AbortSignal,
): Effect.Effect<Page[], OkraHookError> {
  return sessionPagesEffect(session, signal).pipe(
    Effect.mapError((error) =>
      okraHookFailure('pages', 'Document pages could not be fetched', error.cause),
    ),
  );
}

/**
 * Fetch all pages for a document.
 *
 * ```tsx
 * const { data: pages, isLoading } = usePages(session);
 * // or with a document ID
 * const { data: pages } = usePages('doc-abc123');
 * ```
 */
export function usePages(
  sessionOrId: OkraSession | string | null,
  options: UsePagesOptions = {},
): UsePagesReturn {
  const { enabled = true } = options;
  const { client } = useOkra();

  const [data, setData] = useState<Page[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const session = useMemo(
    () => (typeof sessionOrId === 'string' ? client.sessions.from(sessionOrId) : sessionOrId),
    [client, sessionOrId],
  );
  const owner = useRequestOwner(session, enabled);

  const fetchPages = useCallback(async () => {
    if (!session || !enabled) return;
    const ac = owner.begin();
    if (!ac) return;
    try {
      setIsLoading(true);
      const result = await runOkraHookEffect(fetchPagesEffect(session), ac.signal);
      if (!owner.owns(ac)) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!owner.owns(ac) || (err as Error).name === 'AbortError') return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (owner.owns(ac)) setIsLoading(false);
    }
  }, [session, enabled, owner]);

  // Reset when the selected session changes
  useEffect(() => {
    setData([]);
    setError(null);
  }, [session]);

  useEffect(() => {
    setIsLoading(false);
    void fetchPages();
  }, [fetchPages]);

  return { data, isLoading, error, refetch: fetchPages };
}
